import { useEffect } from 'react';
import { observable, computed } from 'mobx';
import { useLayerData } from '@src/utils/hooks/use-layer-data';
import useModal from '@src/utils/hooks/use-modal';
import AppointmentModal from '@src/business-components/appointment-modal';
import AppointmentModalStore from '@src/business-components/appointment-modal/store';
import AppointmentSuccessModal from '@src/business-components/appointment-success-modal';
import AppointmentSuccessModalStore from '@src/business-components/appointment-success-modal/store';
import CdkModal from '@src/business-components/cdkey-modal';
import CdkModalStore from '@src/business-components/cdkey-modal/store';
import BaseStore from '@src/utils/store/base-store';
import Video from '@src/components/video';
import { noop } from '@src/utils/common-util';
import KvVideoStore from './components/kv-video/store';
import message from '@src/components/common-message';
import { toPage } from '@src/utils/common-util';
import { autobind } from 'core-decorators';
import getLanguageByPath from '@src/utils/locale/getLanguageByPath';
import getPageLocalesData from '@src/global-locales/get-page-locales-data';
import { getLocales } from '@src/global-locales/get-page-locales-data';

const pageLocalesData = getPageLocalesData()?.main?.kvScreen || {};
const language = getLanguageByPath();

/**
 * 下载状态
 */
export const DOWNLOAD_STATUS = {
  open: 'on',
  close: 'off'
};

/**
 * 活动状态
 */
export const ACTIVITY_STATUS = {
  open: '1',
  close: '0'
};

// 典藏礼包链接
const collectorsPackLinkMap = {
  en: 'https://mechabreak.seasungames.com/p/zt/2025/03/21/market/en/index.html#/',
  ja: 'https://mechabreak.seasungames.com/p/zt/2025/03/21/market/ja/index.html#/',
  ko: 'https://mechabreak.seasungames.com/p/zt/2025/03/21/market/ko/index.html#/',
  'zh-Hant': 'https://mechabreak.seasungames.com/p/zt/2025/03/21/market/zh-Hant/index.html#/',
  es: 'https://mechabreak.seasungames.com/p/zt/2025/03/21/market/es/index.html#/',
  pt: 'https://mechabreak.seasungames.com/p/zt/2025/03/21/market/pt/index.html#/',
  de: 'https://mechabreak.seasungames.com/p/zt/2025/03/21/market/de/index.html#/',
  fr: 'https://mechabreak.seasungames.com/p/zt/2025/03/21/market/fr/index.html#/',
  ru: 'https://mechabreak.seasungames.com/p/zt/2025/03/21/market/ru/index.html#/',
  th: 'https://mechabreak.seasungames.com/p/zt/2025/03/21/market/th/index.html#/'
};

class FirstPageStore extends BaseStore {
  constructor({ ...rest } = {}) {
    super();
    Object.assign(this, rest);
  }

  platformBtnConfig = {
    xbox: {
      bg: require('./images/platform-btn/xbox-yuyue.png'),
      leftHover: [
        require('./images/platform-btn/xbox-yuyue-hover-left-0.png'),
        require('./images/platform-btn/xbox-yuyue-hover-left-1.png'),
        require('./images/platform-btn/xbox-yuyue-hover-left-2.png'),
        require('./images/platform-btn/xbox-yuyue-hover-left-3.png')
      ],
      rightHover: [
        require('./images/platform-btn/xbox-yuyue-hover-right-0.png'),
        require('./images/platform-btn/xbox-yuyue-hover-right-1.png'),
        require('./images/platform-btn/xbox-yuyue-hover-right-2.png'),
        require('./images/platform-btn/xbox-yuyue-hover-right-3.png')
      ]
    },
    steam: {
      bg: require('./images/platform-btn/steam-yuyue.png'),
      leftHover: [
        require('./images/platform-btn/steam-yuyue-hover-left-0.png'),
        require('./images/platform-btn/steam-yuyue-hover-left-1.png'),
        require('./images/platform-btn/steam-yuyue-hover-left-2.png'),
        require('./images/platform-btn/steam-yuyue-hover-left-3.png')
      ],
      rightHover: [
        require('./images/platform-btn/steam-yuyue-hover-right-0.png'),
        require('./images/platform-btn/steam-yuyue-hover-right-1.png'),
        require('./images/platform-btn/steam-yuyue-hover-right-2.png'),
        require('./images/platform-btn/steam-yuyue-hover-right-3.png')
      ]
    }
  };

  videoClose = noop;

  kvVideoStore = new KvVideoStore();

  @observable
  layerData = {
    /**
     * 是否开启下载
     */
    download_status: '',
    /**
     * 下载链接
     */
    download_link: 'https://ssg-tx-os.seasuncdn.com/ssglauncher/gp/Client/prod/Channel/MechaBREAKGP/SeasunGameInstaller-MechaBREAKGP-mechabreak_gp.exe',
    /**
     * 预约链接
     */
    appointmentLink: '',
    /**
     * steam愿望表单链接
     */
    steam_link: '',
    /**
     * xbox愿望表单链接
     */
    xbox_link: '',
    /**
     * 视频播放封面
     */
    videoCover: '',
    /**
     * 视频播放链接
     */
    videoLink: '',
    /**
     * 礼包入口封面
     */
    gifts_entry_cover: '',
    /**
     * 礼包跳转链接
     */
    gifts_link: '',
    /**
     * 活动入口封面
     */
    activity_entry_cover: '',
    /**
     * 活动报名跳转链接
     */
    activityLink: '',
    /**
     * 活动列表
     */
    activityList: []
  };

  @observable
  localData = {
    /**
     * kv背景图地址
     */
    kv_url: require(`./images/${language}/kv.jpg`),
    kvVideoLink: 'https://static-omni-mechabreak.seasungames.com/uploads/202511/1763453452961_1117_S2_final_KV%EF%BC%8850m%EF%BC%89.mp4',
    /**
     * slogan标语图片
     */
    slogan: require(`./images/${language}/slogan.png`),
    /**
     * 下载按钮文案
     */
    downloadText: pageLocalesData.downloadText,
    /**
     * 预约按钮文案
     */
    appointmentText: getLocales('reserve.button'),
    /**
     * steam商店页按钮文案
     */
    steamWishBtnText: pageLocalesData.steamWishBtnText,
    /**
     * xbox商店页按钮文案
     */
    xboxWishBtnText: pageLocalesData.xboxWishBtnText,
    /**
     * steam标语LOGO
     */
    steam_logo: require('./images/first-page-layer-3.png'),
    /**
     * xbox logo
     */
    xbox_logo: require('./images/first-page-layer-2.png'),
    /**
     * google play logo
     */
    gpg_logo: require('./images/platform-google-play.png'),
    /**
     * 典藏礼包链接
     */
    giftLink: collectorsPackLinkMap[language]
  };

  // 典藏礼包活动是否已结束
  @observable isGiftActivityOver = false;

  // 活动列表
  @observable activityList = [];

  // 预约人数总数
  @computed
  get appointmentCount() {
    return this.$globalStore.appointmentCount;
  }

  // 是否可以领取福利码
  @computed
  get isCanClaimCdk() {
    return (
      (this.$globalStore.isAppointmentStage && +this.$globalStore.isInSubscribeCodeTime === 1) ||
      (this.$globalStore.isDownloadStage && +this.$globalStore.downLoadGetCodeStatus === 1)
    );
  }

  @computed
  get isDownloadStage() {
    return this.$globalStore.isDownloadStage;
  }

  // 预约弹窗
  appointmentModal = null;

  appointmentModalStore = new AppointmentModalStore({
    $main: this,
    closeCallback: () => this.kvVideoStore.play(),
    appointmentSuccessCallBack: email => {
      this.appointmentSuccessModalStore.email = email;
    }
  });

  // 预约成功弹窗
  appointmentSuccessModal = null;

  appointmentSuccessModalStore = new AppointmentSuccessModalStore({ $main: this });

  // 礼包弹窗
  cdkModal = null;

  cdkModalStore = new CdkModalStore({ $main: this });

  onVideoPlay = () => {
    const { videoLink } = this.layerData;
    if (videoLink) {
      this.kvVideoStore.pause();
      const { close } = Video.append({
        src: videoLink,
        closeCb: () => {
          this.kvVideoStore.play();
        }
      });
      this.videoClose = close;
    }
  };

  @autobind
  toDownloadPage() {
    toPage('download', false, this.redirect);
  }

  // cdk领取
  claimCdk = () => {
    this.cdkModal.open();
  };

  // 立即预约
  onAppointment = () => {
    const { accountInfo, inAppointmentTime } = this.$globalStore;
    this.kvVideoStore.pause();
    if (inAppointmentTime && accountInfo) {
      message.success(pageLocalesData.preRegisterTips);
    } else {
      this.appointmentModal.open({
        extraStore: {
          appointmentSuccessCallBack: () => {
            this.appointmentSuccessModal.open();
          }
        }
      });
    }
  };

  // 下载礼包领取
  getDownloadGift = () => {
    this.cdkModal.open();
  };

  use() {
    super.use();

    this.appointmentModal = useModal(AppointmentModal, { store: this.appointmentModalStore });

    this.appointmentSuccessModal = useModal(AppointmentSuccessModal, { store: this.appointmentSuccessModalStore });

    this.cdkModal = useModal(CdkModal, { store: this.cdkModalStore });

    const funlinkData = useLayerData('part1');

    useEffect(() => {
      return () => {
        this.videoClose();
      };
    }, []);

    useEffect(
      () => {
        if (funlinkData) {
          this.layerData = funlinkData;
          this.activityList = funlinkData.activityList || [];
        }
      },
      [funlinkData]
    );
  }
}
export default FirstPageStore;
